<script setup lang="ts">
import type { JobWithMetaDescription } from "@/types";
import { generateJobUrl } from "@/utils/job";

interface Props {
  job: JobWithMetaDescription;
  iframe?: boolean;
  parentSource?: string;
}

const props = defineProps<Props>();

const { sanitizeHtml } = useSanitize();

const jobUrl = computed(() => {
  return generateJobUrl(props.job, props.parentSource);
});

const sanitizedMetaDescription = computed(() => sanitizeHtml(props.job.meta_description?.meta_description));
const sanitizedDescription = computed(() => sanitizeHtml(props.job.description));
</script>

<template>
  <NuxtLink
    :to="jobUrl"
    :target="iframe ? '_blank' : '_self'"
    :rel="iframe ? 'noopener noreferrer' : undefined"
    class="group block bg-white rounded-2xl 2xl:rounded-3xl border-2 border-sand/50 shadow-warm hover:shadow-warm-lg hover:border-primary/30 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
  >
    <div class="p-6 2xl:p-8 3xl:p-10 4xl:p-12">
      <div class="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
        <!-- Company Logo (if available) -->
        <div class="hidden lg:block flex-shrink-0">
          <div class="w-14 h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 rounded-xl 2xl:rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
            <Icon name="material-symbols:work" class="w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-primary" />
          </div>
        </div>

        <div class="flex-1 min-w-0">
          <!-- Job title -->
          <h3 class="font-secondary font-bold text-dark-gray text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl mb-3 2xl:mb-4 line-clamp-2 group-hover:text-primary transition-colors duration-300">
            {{ job.title }}
          </h3>

          <!-- Job tags -->
          <div class="flex flex-wrap items-center gap-2 2xl:gap-3 mb-4 2xl:mb-5">
            <!-- Location -->
            <div class="inline-flex items-center gap-1.5 px-3 py-1.5 2xl:px-4 2xl:py-2 bg-savanna/10 text-savanna rounded-full text-xs 2xl:text-sm 3xl:text-base font-medium">
              <Icon name="material-symbols:location-on" class="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />
              <span class="truncate max-w-[180px] 2xl:max-w-[220px]">{{ job.location.formatted_address }}</span>
            </div>

            <!-- Capacity/Employment Type -->
            <div v-if="job.capacity" class="inline-flex items-center gap-1.5 px-3 py-1.5 2xl:px-4 2xl:py-2 bg-secondary/10 text-secondary rounded-full text-xs 2xl:text-sm 3xl:text-base font-medium">
              <Icon name="material-symbols:schedule" class="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />
              <span>{{ job.capacity.name }}</span>
            </div>

            <!-- Experience Level -->
            <div v-if="job.experience" class="inline-flex items-center gap-1.5 px-3 py-1.5 2xl:px-4 2xl:py-2 bg-primary/10 text-primary rounded-full text-xs 2xl:text-sm 3xl:text-base font-medium">
              <Icon name="material-symbols:trending-up" class="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />
              <span>{{ job.experience.name }}</span>
            </div>

            <!-- Working Model -->
            <div v-if="job.working_model" class="inline-flex items-center gap-1.5 px-3 py-1.5 2xl:px-4 2xl:py-2 bg-terracotta/10 text-terracotta rounded-full text-xs 2xl:text-sm 3xl:text-base font-medium">
              <Icon name="material-symbols:home-work" class="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />
              <span>{{ job.working_model.name }}</span>
            </div>
          </div>

          <!-- Description preview -->
          <div v-if="job.meta_description" class="text-medium-gray text-sm 2xl:text-base 3xl:text-lg leading-relaxed line-clamp-2 mb-4 2xl:mb-5">
            <div v-html="sanitizedMetaDescription" />
          </div>
          <div v-else class="prose prose-sm 2xl:prose-base text-medium-gray min-w-full line-clamp-2 mb-4 2xl:mb-5" v-html="sanitizedDescription" />

          <!-- Footer: Salary + CTA -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 2xl:pt-5 border-t border-sand/50">
            <!-- Salary -->
            <div v-if="formatSalary(job, true)" class="flex items-center gap-2">
              <div class="w-8 h-8 2xl:w-10 2xl:h-10 rounded-lg 2xl:rounded-xl bg-savanna/10 flex items-center justify-center">
                <Icon name="material-symbols:payments" class="w-4 h-4 2xl:w-5 2xl:h-5 text-savanna" />
              </div>
              <span class="font-secondary font-bold text-savanna text-base 2xl:text-lg 3xl:text-xl">
                {{ formatSalary(job, true) }}
              </span>
            </div>
            <div v-else class="flex-1"></div>

            <!-- View Job CTA -->
            <div class="inline-flex items-center gap-2 text-primary font-semibold text-sm 2xl:text-base group-hover:text-primary-dark transition-colors">
              <span>View Details</span>
              <Icon
                name="material-symbols:arrow-forward"
                class="w-5 h-5 2xl:w-6 2xl:h-6 group-hover:translate-x-1 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
